/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (BUGS 154): each call model created in a page goes to Graylog with
 * where its LiveKit transport came from and whether config.json loaded.
 * A second model in one page means the call was set up anew (the first
 * one closed); a missing transport then shows why.
 */

import { take, type Observable } from "rxjs";

import { type Graylog } from "../logging/Graylog";
import { configLoad } from "./startup";

/** Where the transport of a call came from. */
export type TransportSource = "backend" | "config" | "custom" | "none";

let attempts = 0;
let lastSource: TransportSource | null = null;

/** Called by transport discovery with what it found. */
export function noteTransportSource(source: TransportSource): void {
  lastSource = source;
}

/**
 * Counts a new call model and, once its transport is known (or not
 * found), sends "Call model created" to Graylog.
 */
export function reportCallModel(
  graylog: Graylog,
  transport$: Observable<{ transport: { livekit_service_url: string } }>,
  now: () => number = Date.now,
): void {
  attempts += 1;
  const attempt = attempts;
  const started = now();
  const send = (fields: Record<string, unknown>): void =>
    graylog.logInfo("Call model created", {
      attempt,
      config: configLoad(),
      transportSource: lastSource ?? "unknown",
      transportSeconds: Math.round((now() - started) / 100) / 10,
      ...fields,
    });
  transport$.pipe(take(1)).subscribe({
    next: ({ transport }) =>
      send({ transportUrl: transport.livekit_service_url }),
    error: (e: unknown) =>
      send({
        transportSource: lastSource ?? "none",
        error: e instanceof Error ? e.name : String(e),
      }),
  });
}

/** For tests: a fresh page. */
export function resetCallModelCount(): void {
  attempts = 0;
  lastSource = null;
}
